// Module Secteurs : référentiel des secteurs de service RÉCA (docs/secteurs/README.md §2.1).
// Un secteur est une zone commerciale éditable, jamais une limite municipale ni une emprise calculée de Territoire.
// Géométries GeoJSON en WGS84, positions [longitude, latitude] (même convention que `shared/domain/zoneEditor`).

import { z } from 'zod'

export const sectorServices = ['RESIDENTIAL', 'COMMERCIAL', 'MULTI_UNIT'] as const
export type SectorService = (typeof sectorServices)[number]

export const sectorServiceLabel: Record<SectorService, string> = {
  RESIDENTIAL: 'Résidentiel',
  COMMERCIAL: 'Commercial',
  MULTI_UNIT: 'Multilogements',
}

export const availabilityStatuses = ['AVAILABLE', 'LIMITED', 'WAITLIST', 'FULL', 'NOT_SERVED'] as const
export type AvailabilityStatus = (typeof availabilityStatuses)[number]

export const availabilityStatusLabel: Record<AvailabilityStatus, string> = {
  AVAILABLE: 'Disponible',
  LIMITED: 'Places limitées',
  WAITLIST: "Liste d'attente",
  FULL: 'Complet',
  NOT_SERVED: 'Non desservi',
}

/** Effet d'un statut dans le formulaire public (maquette 1g). */
export const availabilityStatusDescription: Record<AvailabilityStatus, string> = {
  AVAILABLE: 'Demande de soumission normale.',
  LIMITED: 'Demande possible, disponibilité limitée affichée.',
  WAITLIST: 'Le formulaire présente une inscription sur liste d’attente.',
  FULL: 'Aucune promesse de place. Demande d’information distincte.',
  NOT_SERVED: 'Message hors secteur, évaluation exceptionnelle possible.',
}

/** Texte public par défaut de chaque statut (maquette 1t); un texte saisi le remplace. */
export const availabilityDefaultText: Record<AvailabilityStatus, string> = {
  AVAILABLE: 'Nous desservons votre adresse cet hiver.',
  LIMITED:
    'Places limitées cette saison. Faites votre demande, nous vous confirmons la disponibilité rapidement.',
  WAITLIST:
    'Ce service est complet pour l’instant. Inscrivez-vous : nous vous contactons dès qu’une place se libère.',
  FULL: 'Aucune place disponible cette saison. Vous pouvez nous écrire pour obtenir de l’information, sans réservation de place.',
  NOT_SERVED:
    'Cette adresse est hors de nos secteurs de service. Nous pouvons évaluer votre demande à titre exceptionnel.',
}

/** Longueur maximale du texte public saisi à l'écran (maquette 1g); la base accepte jusqu'à 280. */
export const availabilityPublicTextMax = 140

export const areaProvenances = ['MANUAL', 'REFERENCE_COPY'] as const
export type AreaProvenance = (typeof areaProvenances)[number]

export const areaProvenanceLabel: Record<AreaProvenance, string> = {
  MANUAL: 'Zone dessinée par RÉCA',
  REFERENCE_COPY: "Copie d'une limite de référence",
}

const position = z.tuple([z.number(), z.number()])
const ring = z.array(position)

export const sectorGeometrySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('Polygon'), coordinates: z.array(ring) }),
  z.object({ type: z.literal('MultiPolygon'), coordinates: z.array(z.array(ring)) }),
])
export type SectorGeometry = z.infer<typeof sectorGeometrySchema>

/** Saison canonique `AAAA-AAAA` sur deux années consécutives (même forme que `contracts.saison`). */
export function isSeasonLabel(value: string): boolean {
  const match = /^(\d{4})-(\d{4})$/.exec(value)
  return match !== null && Number(match[2]) === Number(match[1]) + 1
}

const seasonSchema = z.string().refine(isSeasonLabel)

export const sectorSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  color: z.string(),
  internalDescription: z.string().nullable(),
  archivedAt: z.string().nullable(),
  updatedAt: z.string(),
  draftArea: z
    .object({
      geometry: sectorGeometrySchema.nullable(),
      provenance: z.enum(areaProvenances),
      referenceBoundaryId: z.string().nullable(),
      provenanceNote: z.string().nullable(),
      updatedAt: z.string(),
    })
    .nullable(),
  publishedArea: sectorGeometrySchema.nullable(),
})
export type Sector = z.infer<typeof sectorSchema>

const availabilityCellSchema = z.object({
  sectorId: z.string(),
  service: z.enum(sectorServices),
  season: seasonSchema,
  status: z.enum(availabilityStatuses),
  publicText: z.string().nullable(),
})

export const draftAvailabilitySchema = availabilityCellSchema.extend({
  internalNote: z.string().nullable(),
  updatedAt: z.string(),
})
export type DraftAvailability = z.infer<typeof draftAvailabilitySchema>

export const publishedAvailabilitySchema = availabilityCellSchema
export type PublishedAvailability = z.infer<typeof publishedAvailabilitySchema>

export const referenceBoundarySchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['MUNICIPALITY', 'FORMER_MUNICIPALITY', 'OTHER']),
  sourceName: z.string(),
  sourceUrl: z.string().nullable(),
  license: z.string().nullable(),
  sourceDate: z.string().nullable(),
  retrievedAt: z.string(),
  geometry: sectorGeometrySchema,
})
export type ReferenceBoundary = z.infer<typeof referenceBoundarySchema>

export const sectorWorkspaceSchema = z.object({
  revision: z.number().int().nonnegative(),
  basePublicationId: z.string().nullable(),
  latestPublication: z
    .object({
      id: z.string(),
      version: z.number().int().positive(),
      publicSeason: seasonSchema,
      reason: z.string(),
      publishedAt: z.string(),
      publishedBy: z.string(),
    })
    .nullable(),
  sectors: z.array(sectorSchema),
  draftAvailability: z.array(draftAvailabilitySchema),
  publishedAvailability: z.array(publishedAvailabilitySchema),
  referenceBoundaries: z.array(referenceBoundarySchema),
})
export type SectorWorkspace = z.infer<typeof sectorWorkspaceSchema>

/** Chaque écriture porte la révision connue : une révision périmée est refusée (`SECTOR_DRAFT_CONFLICT`). */
interface RevisionGuard {
  expectedRevision: number
}

export interface CreateSectorCommand extends RevisionGuard {
  code: string
  name: string
  color: string
  internalDescription?: string
}

export interface UpdateSectorCommand extends RevisionGuard {
  sectorId: string
  name?: string
  color?: string
  internalDescription?: string | null
}

export interface SetSectorArchivedCommand extends RevisionGuard {
  sectorId: string
  archived: boolean
}

export interface SaveSectorAreaCommand extends RevisionGuard {
  sectorId: string
  /** Nul : le brouillon n'a pas de contour; la version publiée n'est pas touchée. */
  geometry: SectorGeometry | null
  provenance?: AreaProvenance
  referenceBoundaryId?: string
  provenanceNote?: string
}

export interface AvailabilityChange {
  sectorId: string
  service: SectorService
  season: string
  /** Nul : retire la cellule du brouillon. Une disponibilité absente n'est jamais « Disponible ». */
  status: AvailabilityStatus | null
  publicText?: string | null
  internalNote?: string | null
}

export interface SaveAvailabilityCommand extends RevisionGuard {
  items: AvailabilityChange[]
}

export interface CopyReferenceCommand extends RevisionGuard {
  sectorId: string
  referenceBoundaryId: string
}

export const geometryIssueCodes = [
  'TOO_FEW_VERTICES',
  'INVALID',
  'EMPTY_AREA',
  'TOO_COMPLEX',
  'SUSPECT_COORDINATE_ORDER',
] as const
export type GeometryIssueCode = (typeof geometryIssueCodes)[number]

export const geometryIssueLabel: Record<GeometryIssueCode, string> = {
  TOO_FEW_VERTICES: 'Un contour compte au moins trois sommets.',
  INVALID: 'Contour invalide (croisement, trou hors du contour ou parties superposées).',
  EMPTY_AREA: 'Surface nulle.',
  TOO_COMPLEX: 'Contour trop détaillé (plus de 10 000 sommets).',
  SUSPECT_COORDINATE_ORDER: 'Latitude et longitude semblent inversées.',
}

export const geometryIssueSchema = z.object({
  code: z.enum(geometryIssueCodes),
  /** Raison PostGIS (« Self-intersection »…), pour `INVALID` seulement. */
  reason: z.string().optional(),
  /** Position du défaut, [longitude, latitude]. */
  location: position.nullable().optional(),
  vertices: z.number().optional(),
})
export type GeometryIssue = z.infer<typeof geometryIssueSchema>

/** Validation serveur (PostGIS) du brouillon : seule autorité avant publication. */
export const draftValidationSchema = z.object({
  revision: z.number().int().nonnegative(),
  sectors: z.array(
    z.object({ sectorId: z.string(), hasGeometry: z.boolean(), issues: z.array(geometryIssueSchema) }),
  ),
  overlaps: z.array(
    z.object({ sectorIds: z.tuple([z.string(), z.string()]), areaM2: z.number().nonnegative() }),
  ),
})
export type DraftValidation = z.infer<typeof draftValidationSchema>

/** Tolérance de frontière (décision du propriétaire, 25 octobre 2026); le serveur fait foi. */
export const sectorBoundaryToleranceM = 10

export const classificationStatuses = ['matched', 'boundary_review', 'outside', 'unverified'] as const
export type ClassificationStatus = (typeof classificationStatuses)[number]
/** Statut retenu pour une adresse : la correction d'un administrateur donne `manual`. */
export type EffectiveSectorStatus = ClassificationStatus | 'manual'

export const effectiveSectorStatusLabel: Record<EffectiveSectorStatus, string> = {
  matched: 'Secteur attribué',
  boundary_review: 'À confirmer (limite)',
  outside: 'Hors secteur',
  unverified: 'Position à vérifier',
  manual: 'Secteur corrigé',
}

/** Qualité de la position classée : seule une adresse localisée ou épinglée est attribuée. */
export type PositionQuality = 'ADDRESS' | 'MANUAL' | 'APPROXIMATE'

/** `DRAFT` : contours du brouillon; `PUBLISHED` : dernière version publiée. */
export type SectorSource = 'DRAFT' | 'PUBLISHED'

export const classificationSchema = z.object({
  status: z.enum(classificationStatuses),
  sectorId: z.string().nullable(),
  reason: z
    .enum(['NO_POSITION', 'IMPRECISE_POSITION', 'INVALID_POSITION', 'SHARED_BOUNDARY', 'NEAR_EDGE'])
    .nullable(),
  candidates: z.array(
    z.object({ sectorId: z.string(), distanceM: z.number(), boundaryDistanceM: z.number() }),
  ),
})
export type Classification = z.infer<typeof classificationSchema>

export const serviceLocationSectorSchema = z.object({
  serviceLocationId: z.string(),
  contractId: z.string(),
  contractNumber: z.string().nullable(),
  contractStatus: z.string().nullable(),
  contractSeason: z.string().nullable(),
  serviceStatus: z.string(),
  address: z.string(),
  longitude: z.number().nullable(),
  latitude: z.number().nullable(),
  routeIds: z.array(z.string()),
  automatic: classificationSchema,
  override: z.object({ sectorId: z.string(), reason: z.string(), setAt: z.string() }).nullable(),
  /** Secteur retenu : la correction si elle existe, sinon la valeur automatique. */
  sectorId: z.string().nullable(),
  status: z.enum([...classificationStatuses, 'manual']),
})
export type ServiceLocationSector = z.infer<typeof serviceLocationSectorSchema>

export const routeSectorsSchema = z.object({
  routeId: z.string(),
  name: z.string(),
  color: z.string(),
  season: z.string().nullable(),
  /** Ancien champ texte `routes.secteur`, conservé tel quel pendant la transition. */
  legacySector: z.string().nullable(),
  locations: z.number().int().nonnegative(),
  sectors: z.array(z.object({ sectorId: z.string(), locations: z.number().int().positive() })),
  boundaryReview: z.number().int().nonnegative(),
  outside: z.number().int().nonnegative(),
  unverified: z.number().int().nonnegative(),
})
export type RouteSectors = z.infer<typeof routeSectorsSchema>

export interface SetLocationSectorOverrideCommand {
  serviceLocationId: string
  /** Nul : retire la correction, retour à la valeur automatique. */
  sectorId: string | null
  reason: string
}

const sectorChangeSchema = z.object({
  sectorId: z.string(),
  name: z.string(),
  change: z.enum(['ADDED', 'REMOVED', 'GEOMETRY_CHANGED', 'RENAMED']),
  areaBeforeM2: z.number().nullable(),
  areaAfterM2: z.number().nullable(),
  verticesBefore: z.number(),
  verticesAfter: z.number(),
  hasContour: z.boolean(),
})

const availabilityChangeSchema = z.object({
  sectorId: z.string(),
  service: z.enum(sectorServices),
  season: z.string(),
  before: z.enum(availabilityStatuses).nullable(),
  after: z.enum(availabilityStatuses).nullable(),
  publicTextChanged: z.boolean(),
})

/** Comparatif calculé par le serveur entre la dernière version publiée et le brouillon (maquette 1h). */
export const publicationPreviewSchema = z.object({
  basePublicationId: z.string().nullable(),
  baseVersion: z.number().int().nullable(),
  draftRevision: z.number().int().nonnegative(),
  draftBasePublicationId: z.string().nullable(),
  hasChanges: z.boolean(),
  sectors: z.array(sectorChangeSchema),
  availability: z.array(availabilityChangeSchema),
  addresses: z.object({
    added: z.number().int(),
    removed: z.number().int(),
    located: z.number().int(),
    bySector: z.array(z.object({ sectorId: z.string(), added: z.number().int(), removed: z.number().int() })),
  }),
  contractsConcerned: z.number().int(),
  exceptions: z.array(
    z.object({
      contractId: z.string(),
      contractNumber: z.string().nullable(),
      serviceLocationId: z.string(),
      address: z.string(),
      previousSectorId: z.string().nullable(),
    }),
  ),
  leadsChanged: z.number().int(),
  unlocated: z.number().int(),
  blocking: z.array(
    z.object({
      code: z.enum(['GEOMETRY_INVALID', 'OVERLAP']),
      sectorId: z.string().optional(),
      sectorIds: z.array(z.string()).optional(),
      areaM2: z.number().optional(),
      issues: z.array(geometryIssueSchema).optional(),
    }),
  ),
  warnings: z.array(
    z.object({
      code: z.enum(['AVAILABILITY_WITHOUT_CONTOUR', 'LARGE_CHANGE']),
      sectorId: z.string().optional(),
      moved: z.number().optional(),
      located: z.number().optional(),
    }),
  ),
  computedAt: z.string(),
})
export type PublicationPreview = z.infer<typeof publicationPreviewSchema>

export interface PublishCommand {
  /** Révision du brouillon relu : un brouillon modifié depuis est refusé. */
  expectedRevision: number
  /** Version publiée sur laquelle porte la relecture; nul s'il n'y en a aucune. */
  basePublicationId: string | null
  publicSeason: string
  reason: string
}

export const publishResultSchema = z.object({
  publicationId: z.string(),
  version: z.number().int().positive(),
  revision: z.number().int().nonnegative(),
})
export type PublishResult = z.infer<typeof publishResultSchema>

export const requestIntents = ['QUOTE', 'WAITLIST', 'INFO', 'EXCEPTION_REVIEW'] as const
export type RequestIntent = (typeof requestIntents)[number]

export const requestIntentLabel: Record<RequestIntent, string> = {
  QUOTE: 'Demande de soumission',
  WAITLIST: 'Inscription sur liste d’attente',
  INFO: 'Demande d’information',
  EXCEPTION_REVIEW: 'Évaluation hors secteur',
}

export const leadSectorAssessmentKindLabel = {
  CAPTURE: 'À la réception',
  RECHECK: 'Nouvelle évaluation',
  MANUAL: 'Correction manuelle',
  MANUAL_CLEARED: 'Correction retirée',
} as const

/** Secteur d'une demande (lot 8) : état courant et historique, valeur automatique conservée. */
/** Secteur retenu d'une demande, sans historique : colonne « Secteur » de la liste des demandes. */
export const leadSectorBriefSchema = z.object({
  leadId: z.string(),
  sectorId: z.string().nullable(),
  status: z.enum([...classificationStatuses, 'manual']).nullable(),
})
export type LeadSectorBrief = z.infer<typeof leadSectorBriefSchema>

/** Libellé du secteur dans une liste : nom et couleur du secteur retenu, sinon le statut. */
export interface LeadSectorListLabel {
  text: string
  detail?: string
  color?: string
}

export function leadSectorListLabel(
  brief: LeadSectorBrief | undefined,
  sectors: readonly Pick<Sector, 'id' | 'name' | 'color'>[],
): LeadSectorListLabel {
  if (!brief?.status) return { text: 'Non évaluée' }
  const sector = brief.sectorId ? sectors.find((candidate) => candidate.id === brief.sectorId) : undefined
  if (!sector) return { text: effectiveSectorStatusLabel[brief.status] }
  const detail =
    brief.status === 'manual'
      ? 'Corrigé à la main'
      : brief.status === 'matched'
        ? undefined
        : effectiveSectorStatusLabel[brief.status]
  return { text: sector.name, color: sector.color, ...(detail ? { detail } : {}) }
}

export const leadSectorSummarySchema = z.object({
  leadId: z.string(),
  hasPosition: z.boolean(),
  sectorId: z.string().nullable(),
  sectorName: z.string().nullable(),
  status: z.enum([...classificationStatuses, 'manual']).nullable(),
  source: z.enum(['auto', 'manual']).nullable(),
  checkedAt: z.string().nullable(),
  intentSectorId: z.string().nullable(),
  intentSectorName: z.string().nullable(),
  requestIntent: z.enum(requestIntents).nullable(),
  history: z.array(
    z.object({
      kind: z.enum(['CAPTURE', 'RECHECK', 'MANUAL', 'MANUAL_CLEARED']),
      createdAt: z.string(),
      status: z.enum([...classificationStatuses, 'manual']),
      reason: z.string().nullable(),
      sectorId: z.string().nullable(),
      sectorName: z.string().nullable(),
      availabilityStatus: z.enum(availabilityStatuses).nullable(),
      publicationVersion: z.number().int().nullable(),
      presented: z.record(z.string(), z.unknown()),
      presentedChanged: z.boolean().nullable(),
      note: z.string().nullable(),
      actorId: z.string().nullable(),
    }),
  ),
})
export type LeadSectorSummary = z.infer<typeof leadSectorSummarySchema>

export interface SetLeadSectorCommand {
  leadId: string
  /** Nul : retour à la dernière évaluation automatique. */
  sectorId: string | null
  reason: string
}

/** Historique (maquette 1j) : brouillon courant, versions publiées, brouillons archivés avant une restauration. */
export const sectorHistorySchema = z.object({
  draft: z.object({
    revision: z.number().int().nonnegative(),
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
    basePublicationId: z.string().nullable(),
    restoredFromVersion: z.number().int().nullable(),
  }),
  publications: z.array(
    z.object({
      id: z.string(),
      version: z.number().int().positive(),
      publishedAt: z.string(),
      publishedBy: z.string().nullable(),
      reason: z.string(),
      publicSeason: z.string(),
      restoredFromVersion: z.number().int().nullable(),
      sectorChanges: z.number().int().nonnegative(),
      availabilityChanges: z.number().int().nonnegative(),
    }),
  ),
  snapshots: z.array(
    z.object({
      id: z.string(),
      revision: z.number().int(),
      reason: z.string(),
      createdAt: z.string(),
      createdBy: z.string().nullable(),
    }),
  ),
})
export type SectorHistory = z.infer<typeof sectorHistorySchema>

/** Contenu d'une version publiée, pour la consulter (lecture seule). */
export const publicationContentSchema = z.object({
  id: z.string(),
  version: z.number().int().positive(),
  publicSeason: z.string(),
  publishedAt: z.string(),
  publishedBy: z.string().nullable(),
  reason: z.string(),
  sectors: z.array(
    z.object({
      sectorId: z.string(),
      code: z.string(),
      name: z.string(),
      color: z.string(),
      provenance: z.enum(areaProvenances),
      geometry: sectorGeometrySchema.nullable(),
    }),
  ),
  availability: z.array(publishedAvailabilitySchema),
})
export type PublicationContent = z.infer<typeof publicationContentSchema>

export interface RestorePublicationCommand {
  expectedRevision: number
  publicationId: string
  reason: string
}

export const sectorWriteResultSchema = z.object({
  sectorId: z.string().optional(),
  revision: z.number().int().nonnegative(),
})
export type SectorWriteResult = z.infer<typeof sectorWriteResultSchema>

/** Code public : minuscules, chiffres et tirets simples (adresse des pages du site). */
export function isSectorCode(value: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value) && value.length <= 60
}

/** Proposition de code à partir du nom : « Saint-Jérôme — secteur » → `saint-jerome-secteur`. */
export function sectorCodeFromName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '')
}

export const sectorErrorMessage: Record<string, string> = {
  SECTOR_READ_FORBIDDEN: "Vous n'avez pas accès aux secteurs.",
  SECTOR_EDIT_FORBIDDEN: "Vous n'avez pas le droit de modifier les secteurs.",
  SECTOR_DRAFT_CONFLICT:
    "Le brouillon a été modifié par quelqu'un d'autre. Rechargez pour voir ses changements.",
  SECTOR_NOT_FOUND: 'Ce secteur est introuvable.',
  SECTOR_ARCHIVED: 'Ce secteur est archivé : réactivez-le pour le modifier.',
  SECTOR_CODE_INVALID: 'Le code ne contient que des minuscules, des chiffres et des tirets.',
  SECTOR_CODE_TAKEN: 'Ce code est déjà utilisé par un autre secteur.',
  SECTOR_NAME_INVALID: 'Le nom doit compter entre 1 et 100 caractères.',
  SECTOR_NAME_TAKEN: 'Un secteur actif porte déjà ce nom.',
  SECTOR_COLOR_INVALID: 'Couleur invalide.',
  SECTOR_DESCRIPTION_TOO_LONG: 'La description interne dépasse 2000 caractères.',
  SECTOR_PROVENANCE_INVALID: 'Provenance du contour invalide.',
  SECTOR_REFERENCE_NOT_FOUND: 'Limite de référence introuvable.',
  SECTOR_GEOMETRY_TYPE_INVALID: 'Le contour doit être un polygone.',
  SECTOR_GEOMETRY_UNREADABLE: 'Contour illisible.',
  SECTOR_GEOMETRY_EMPTY: 'Le contour est vide.',
  SECTOR_NO_PUBLICATION: 'Aucune version publiée pour le moment.',
  SECTOR_SOURCE_INVALID: 'Source de contours inconnue.',
  SECTOR_OVERRIDE_REASON_REQUIRED: 'Indiquez le motif de la correction.',
  SERVICE_LOCATION_NOT_FOUND: 'Adresse de service introuvable.',
  SECTOR_GEOMETRY_RING_NOT_CLOSED: 'Le contour n’est pas fermé : le dernier point doit rejoindre le premier.',
  SECTOR_GEOMETRY_OUT_OF_RANGE: 'Coordonnées hors bornes : vérifiez l’ordre longitude, latitude.',
  AVAILABILITY_SERVICE_INVALID: 'Service inconnu.',
  AVAILABILITY_SEASON_INVALID: 'Saison invalide (format AAAA-AAAA).',
  AVAILABILITY_STATUS_INVALID: 'Statut de disponibilité inconnu.',
  AVAILABILITY_PUBLIC_TEXT_TOO_LONG: 'Le texte public dépasse 280 caractères.',
  AVAILABILITY_INTERNAL_NOTE_TOO_LONG: 'La note interne dépasse 2000 caractères.',
  SECTOR_PUBLISH_FORBIDDEN:
    'Vous pouvez préparer un brouillon; une personne autorisée à publier devra le relire.',
  SECTOR_PUBLICATION_CONFLICT:
    'Une autre version a été publiée depuis votre relecture. Votre brouillon est conservé : relisez les changements.',
  SECTOR_PUBLICATION_BLOCKED: 'Publication bloquée : corrigez les erreurs signalées.',
  SECTOR_NOTHING_TO_PUBLISH: 'Le brouillon est identique à la version publiée.',
  SECTOR_PUBLIC_SEASON_INVALID: 'Choisissez la saison présentée sur le site.',
  SECTOR_PUBLICATION_REASON_REQUIRED: 'Indiquez le motif de la publication.',
  AVAILABILITY_COMMAND_INVALID: 'Aucune disponibilité à enregistrer.',
  LEAD_NOT_FOUND: 'Demande introuvable.',
  SECTOR_PUBLICATION_NOT_FOUND: 'Version introuvable.',
  SECTOR_RESTORE_REASON_REQUIRED: 'Indiquez le motif de la restauration.',
  NETWORK: 'Connexion impossible. Réessayez.',
  RESPONSE_INVALID: 'Réponse inattendue du serveur.',
}

export function sectorErrorText(code: string): string {
  return sectorErrorMessage[code] ?? 'Erreur inattendue.'
}
